/**
 * @file: ScrollToBottomPill.tsx
 *
 * Floating pill, absolute-positioned at a fixed offset above the input
 * bar — explicitly NOT relative to the message-area wrapper so the
 * pill doesn't slide upward whenever the typing/quote labels appear
 * below the message list. The parent passes the measured input-bar
 * height as `bottomOffset` so the pill keeps its constant gap to the
 * input regardless of what's stacked above it.
 *
 * Idle copy ("回到底部") flips to "N 条新消息" while unread messages
 * stack up out of view. Tap → smooth-scroll to end + reset counter
 * (the FlatList's `onScroll` also resets when the user manually
 * scrolls back into the at-bottom band).
 */

import { Typography } from '@euler/app/components/typography';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChevronsDown } from 'lucide-react-native';
import { MotiView } from 'moti';
import { FC, memo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

export const ScrollToBottomPill: FC<{
  bottomOffset: number;
  newMessageCount: number;
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
}> = memo(({ bottomOffset, newMessageCount, onPress, style }) => {
  const { rpx } = useResponsiveContext();
  const hasUnread = newMessageCount > 0;
  const label = hasUnread ? `${newMessageCount} 条新消息` : '回到底部';
  // Enter: slide up from below + fade in. Exit: reverse. The pill is
  // wrapped by <AnimatePresence/> at the call-site so unmount waits
  // for the exit transition to complete instead of popping out.
  return (
    <MotiView
      pointerEvents="box-none"
      from={{ opacity: 0, translateY: rpx(12) }}
      animate={{ opacity: 1, translateY: 0 }}
      exit={{ opacity: 0, translateY: rpx(12) }}
      transition={{ type: 'timing', duration: 180 }}
      exitTransition={{ type: 'timing', duration: 140 }}
      style={[
        {
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: bottomOffset,
          alignItems: 'center',
        },
        style,
      ]}
    >
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.8}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(8),
          borderRadius: rpx(20),
          backgroundColor: hasUnread ? '#3b82f6' : '#ffffff',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.12,
          shadowRadius: 8,
          elevation: 4,
          borderWidth: hasUnread ? 0 : StyleSheet.hairlineWidth,
          borderColor: '#e5e7eb',
          gap: rpx(6),
        }}
      >
        <ChevronsDown
          size={rpx(16)}
          color={hasUnread ? '#fff' : '#6b7280'}
          strokeWidth={2.5}
        />
        <Typography.Text
          size={rpx(12)}
          color={hasUnread ? '#fff' : '#6b7280'}
          style={{ fontFamily: FontFamily.NotoSans.Medium }}
        >
          {label}
        </Typography.Text>
      </TouchableOpacity>
    </MotiView>
  );
});
